import { Core } from "@faire/design-tokens";
import { Language } from "@faire/web-api--source/indigofair/languages/Language";
import { getFullLanguageName } from "@faire/web/common/consts/THREE_TO_FULL_LANGUAGE_NAME";
import { getTwoLetterLanguageCode } from "@faire/web/common/consts/THREE_TO_TWO_LETTER_LANGUAGE_CODE";
import { useStrictLocalization } from "@faire/web/common/localization";
import {
  hasDuplicateLanguages,
  isLanguageDuplicatedInLocales,
} from "@faire/web/common/localization/languageOptions";
import { localeKeyToFullLanguageName } from "@faire/web/common/localization/localeKeyToFullLanguageName";
import { localeKeyToLocaleString } from "@faire/web/common/localization/localeKeyToLocaleString";
import { Global } from "@faire/web/slate/icons/foundation/Global";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { ILanguageSelectorProps } from "@faire/web/ui/LanguageSelector/common";
import { useMemo } from "react";
import * as React from "react";

export const LanguageSelectorText = ({
  browserLocale,
  browserLanguage,
  availableLocales,
  displayFullLanguageName,
  localeCountryUrlPrefix,
}: ILanguageSelectorProps) => {
  const { strictLocalize } = useStrictLocalization();

  const availableLocalesHasDuplicateLanguages = useMemo(() => {
    return hasDuplicateLanguages(availableLocales);
  }, [availableLocales]);

  const renderSelectedText = () => {
    if (!availableLocalesHasDuplicateLanguages || browserLocale === undefined) {
      return displayFullLanguageName
        ? getFullLanguageName(browserLanguage)
        : getTwoLetterLanguageCode(browserLanguage);
    }

    if (browserLocale.language === Language.LANGUAGE_UNKNOWN) {
      return "";
    }

    const includeCountry =
      browserLocale &&
      browserLocale.language &&
      isLanguageDuplicatedInLocales(
        Language[browserLocale.language],
        availableLocales
      );

    return displayFullLanguageName
      ? localeKeyToFullLanguageName(
          browserLocale,
          includeCountry,
          localeCountryUrlPrefix ?? "en-us"
        )
      : localeKeyToLocaleString({
          localeKey: browserLocale,
          includeCountry: includeCountry,
          lowercaseLanguage: false,
          localeCountryUrlPrefix: localeCountryUrlPrefix ?? "en-us",
        });
  };

  return (
    <>
      <Global
        titleAccess={strictLocalize({
          id: "XyKx+G",
          defaultMessage: "Languages",
          description: {
            text: "Description of icon to change the language for screen-readers",
          },
        })}
        fontSize="16"
        color={Core.text.enabled}
      />
      <Spacer width="1x" />
      <Typography data-test-id="selected-language">
        {renderSelectedText()}
      </Typography>
    </>
  );
};
